import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import Alert from 'react-s-alert';
import { Divisor, Icon, Loading } from 'components';
import { StepIndicator, Step } from 'components/StepIndicator';

import { useAppManager } from 'core/hooks';
import { useClientStore, useRouterStore } from 'apps/hooks';
import { SubscriptionManager } from 'apps/onboarding/subscription/subscriptionManager';
import { CreditCard, Group } from 'apps/upgrade/upgradeFlow/appManager';
import type { TeamMember } from 'apps/onboarding/subscription/subscriptionManager';
import { MAX_ADD_SEATS } from 'constants/subscription';

import { isMobile } from 'styles/utils';

import i18next from 'i18next';
import { isTrialExpired } from 'utils/freeTrials';
import { CreateTeam } from './createTeam';
import { PlanReview } from './planReview/index';
import { AddCollaborators } from './addCollaborators';
import { TransferProjects } from './transferProjects';

interface SubscriptionProps {
  hiddenStepKeys?: string[];
  defaultActiveKey?: string;
  onQueryCurrentRoleInfo?: () => void;
  earlyUpgrade?: boolean;
  currentPeriodEnd: Date;
  loading?: boolean;

  individual?: {
    creditCards: CreditCard[];
    billingCycle: string;
  };
  organization?: {
    groups: Group[];
    creditCards: CreditCard[];
    billingCycle: string;
    purchasedSeats: number;
    includedPlanSeats: number;
    freeSeats: number;
    unusedSeats: number;
    collaborators: { username: string }[];
    teamName: string;
  };
}

export const Subscription = React.memo(
  observer((props: SubscriptionProps) => {
    const subscriptionManager = useAppManager(SubscriptionManager, null, {
      addPaymentSuccessCallback: props.onQueryCurrentRoleInfo,
    });

    const routerStore = useRouterStore();
    const clientStore = useClientStore();

    const { route } = routerStore;
    const { earlyUpgrade, loading } = props;

    const isIndividual =
      route.name === 'onboarding.subscription.individual' || props.individual;

    let stepKeysHidden = isIndividual ? ['createTeam', 'addCollaborators'] : [];

    useEffect(() => {
      if (isIndividual) {
        subscriptionManager.SetActiveKey('review');
      } else {
        subscriptionManager.SetActiveKey('createTeam');
      }

      if (route.params.productType) {
        subscriptionManager.SetProductType(route.params.productType);

        if (clientStore.signedInProfile?.username) {
          const self = subscriptionManager.teamMembers.find(
            (teamMember: TeamMember) => {
              return (
                teamMember.username === clientStore.signedInProfile.username
              );
            },
          );

          if (self) {
            return;
          }

          subscriptionManager.SetTeamMembers([
            {
              username: clientStore.signedInProfile.username,
              role: 'owner',
            },
            ...subscriptionManager.teamMembers,
          ]);

          if (route.name === 'home.newInitiative') {
            subscriptionManager.QueryProfileTransferProjects();
          }
        }
      }
    }, [route.params.productType]);

    // If we got the data from parent
    // we need to set the data to the manager
    useEffect(() => {
      if (props.individual) {
        subscriptionManager.SetCreditCards(props.individual.creditCards);
        subscriptionManager.SetBillingCycle(props.individual.billingCycle);
        subscriptionManager.SetComponentMode('readOnly');
        subscriptionManager.SetAddCardFormVisible(true);
      }

      if (props.organization) {
        const sortGroups = subscriptionManager.SortGroupRoles(
          props.organization.groups,
        );
        subscriptionManager.HandleMembers(
          sortGroups,
          props.organization.collaborators,
        );

        let additionalSeats = 0;

        if (!earlyUpgrade) {
          if (props.organization.freeSeats) {
            if (props.organization.freeSeats > props.organization.unusedSeats) {
              additionalSeats = 0;
            } else {
              additionalSeats =
                props.organization.unusedSeats - props.organization.freeSeats;
            }
          } else {
            additionalSeats = props.organization.unusedSeats;
          }
        }

        const trialTimeExisted = !!clientStore.currentSpaceData?.trialPeriodEnd;
        const trialExpired =
          trialTimeExisted &&
          isTrialExpired(clientStore.currentSpaceData?.trialPeriodEnd);

        if (
          props.organization.creditCards.length === 0 &&
          (!trialTimeExisted || trialExpired)
        ) {
          subscriptionManager.SetAddCardFormVisible(true);
        } else {
          subscriptionManager.SetAddCardFormVisible(false);
        }

        subscriptionManager.SetEarlyUpgrade(earlyUpgrade);

        subscriptionManager.SetAdditionalSeats(additionalSeats);

        subscriptionManager.SetOutsideCollaborators(
          props.organization.collaborators?.length ?? 0,
        );

        subscriptionManager.SetIncludedPlanSeats(
          props.organization.includedPlanSeats + props.organization.freeSeats,
        );
        subscriptionManager.SetTeamName(props.organization.teamName);
        subscriptionManager.SetCreditCards(props.organization.creditCards);

        subscriptionManager.SetComponentMode('readOnly');
      }

      subscriptionManager.SetCurrentPeriodEnd(props.currentPeriodEnd);
    }, [props.individual, props.organization]);

    useEffect(() => {
      if (props.organization?.billingCycle) {
        subscriptionManager.SetBillingCycle(props.organization.billingCycle);
      }
    }, [props.organization?.billingCycle]);

    useEffect(() => {
      if (props.defaultActiveKey) {
        subscriptionManager.SetActiveKey(props.defaultActiveKey);
      }
    }, []);

    const handleChooseEditors = (
      value: string,
      role: 'owner' | 'admin' | 'editor' | 'viewer' | null,
    ) => {
      subscriptionManager.SetTeamMembers(
        subscriptionManager.teamMembers.map((member: TeamMember) => {
          if (member.username === value || member.email === value) {
            return {
              ...member,
              role,
            };
          }
          return member;
        }),
      );
    };

    const handleAddAdditionalEditors = (editorNumber: number) => {
      if (editorNumber > MAX_ADD_SEATS) {
        Alert.error(
          i18next.t(
            `No more than ${MAX_ADD_SEATS} additional editors can be added at a time.`,
          ),
        );
        return;
      }

      subscriptionManager.SetAdditionalSeats(editorNumber);
    };

    const handleBillingCycleChange = (billingCycle: 'yearly' | 'monthly') => {
      subscriptionManager.SetBillingCycle(billingCycle);
    };

    const handleTeamNameChange = (teamName: string) => {
      subscriptionManager.SetTeamName(teamName);
    };

    const handleBack = () => {
      if (routerStore.previousRoute?.name) {
        if (!routerStore.previousRoute.params.spacePath) {
          routerStore.navigate(routerStore.previousRoute.name);
        } else {
          routerStore.navigate(
            routerStore.previousRoute.name,
            routerStore.previousRoute.params,
          );
        }

        return;
      }

      routerStore.navigate('home', {
        spacePath: routerStore.route.params.spacePath,
      });
    };

    if (props.hiddenStepKeys) {
      stepKeysHidden = props.hiddenStepKeys;
    }

    const { productType, formatNextPaymentDate } = subscriptionManager;

    const nextPaymentDate = formatNextPaymentDate('MMM do yyyy');
    const nextPaymentDateForReview = formatNextPaymentDate('LLLL do yyyy');

    if (subscriptionManager.transferProjectsQuery?.loading) {
      return <Loading />;
    }

    return (
      <>
        <div className='grid grid-cols-split-auto-center items-center w-full my-2'>
          <div className='text-black cursor-default self-start h-full flex items-center ml-4'>
            <Icon
              name='arrow_left'
              className='mr-2 cursor-pointer'
              onClick={() => {
                handleBack();
              }}
            />
            <span
              className='font-semibold ml-2 whitespace-nowrap'
              data-testid='subscriptionPage-stepper-back-text'
            >
              {subscriptionManager.productType?.charAt(0).toUpperCase() +
                subscriptionManager.productType?.slice(1)}{' '}
              Plan
            </span>
          </div>

          <StepIndicator
            activeStepKey={subscriptionManager.activeKey}
            hiddenStepKeys={stepKeysHidden}
            onStepClick={subscriptionManager.OnStepClick}
          >
            <Step key='createTeam'>Create Team</Step>
            {!isMobile() && subscriptionManager.projects?.length > 0 && (
              <Step key='transferProjects'>Transfer Projects</Step>
            )}
            <Step key='addCollaborators'>
              {subscriptionManager.componentMode === 'edit'
                ? 'Add Collaborators'
                : 'Review Collaborators'}
            </Step>
            <Step key='review' dataTestId='reviewPlan-step-button'>
              Review
            </Step>
          </StepIndicator>
        </div>
        <Divisor />
        <div className='pb-4 flex flex-col items-center justify-center relative'>
          {subscriptionManager.activeKey === 'createTeam' && (
            <CreateTeam
              teamName={subscriptionManager.teamName}
              onTeamNameChange={handleTeamNameChange}
              onCreateTeamNext={() => {
                subscriptionManager.OnCreateTeamNext();
              }}
              onMemberClick={(value: string) => {
                subscriptionManager.OnMemberClick(value);
              }}
              onAddEmail={subscriptionManager.AddEmail}
              data={subscriptionManager.profiles}
              teamMembers={subscriptionManager.teamMembers?.filter(
                (member: TeamMember) => {
                  return (
                    member.username !== clientStore.signedInProfile.username
                  );
                },
              )}
              removeTeamMember={(name: string) => {
                subscriptionManager.RemoveTeamMember(name);
              }}
              onSearch={(name: string) => {
                subscriptionManager.QueryProfiles(name);
              }}
            />
          )}
          {subscriptionManager.activeKey === 'transferProjects' && (
            <TransferProjects
              teamName={subscriptionManager.teamName}
              projects={subscriptionManager.projects}
              loadingMembersFromProjects={
                subscriptionManager.queryMembersByProjects?.loading ?? false
              }
              onProjectSelected={(projectId: string) => {
                subscriptionManager.OnProjectSelected(projectId);
              }}
              selectedProjects={[...subscriptionManager.selectedProjects]}
              onTransferProjectsNext={() => {
                subscriptionManager.OnTransferProjectsNext();
              }}
            />
          )}
          {subscriptionManager.activeKey === 'addCollaborators' && (
            <AddCollaborators
              componentMode={subscriptionManager.componentMode}
              seats={subscriptionManager.seats}
              teamName={subscriptionManager.teamName}
              outsideCollaborators={subscriptionManager.outsideCollaborators}
              teamMembers={subscriptionManager.teamMembers}
              billingCycle={subscriptionManager.billingCycle}
              totalPrice={subscriptionManager.totalPrice}
              totalEditorsPrice={subscriptionManager.totalEditorsPrice}
              totalIncludedEditorsPrice={
                subscriptionManager.totalIncludedEditorsPrice
              }
              nextPaymentDate={nextPaymentDate}
              includedPlanSeats={subscriptionManager.includedPlanSeats}
              productPrice={subscriptionManager.productPrice}
              viewersNumber={subscriptionManager.viewersNumber}
              billingMonths={subscriptionManager.billingMonths}
              productType={subscriptionManager.productType}
              additionalPrice={subscriptionManager.additionalPrice}
              annualPriceDetails={subscriptionManager.annualPriceDetails}
              additionalSeats={subscriptionManager.additionalSeats}
              onAddAdditionalEditors={handleAddAdditionalEditors}
              onGoToPaymentInformation={() =>
                subscriptionManager.SetActiveKey('review')
              }
              onBillingCycleChange={handleBillingCycleChange}
              onChooseEditors={handleChooseEditors}
            />
          )}
          {subscriptionManager.activeKey === 'review' && (
            <PlanReview
              onAddSeat={() => {
                subscriptionManager.OnAddSeat();
              }}
              nextPaymentDate={nextPaymentDate}
              nextPaymentDateForReview={nextPaymentDateForReview}
              earlyUpgrade={!!earlyUpgrade}
              componentMode={subscriptionManager.componentMode}
              totalPrice={subscriptionManager.totalPrice}
              seats={subscriptionManager.seats}
              billingCycle={subscriptionManager.billingCycle}
              annualPriceDetails={subscriptionManager.annualPriceDetails}
              billingMonths={subscriptionManager.billingMonths}
              productType={subscriptionManager.productType}
              submitting={
                subscriptionManager.submitting ||
                loading ||
                subscriptionManager.newCard?.submitting
              }
              onConfirmChoosePlan={subscriptionManager.UpdatePlan}
              onSwitchProductType={subscriptionManager.SwitchProductType}
              subscriptionConfirmed={subscriptionManager.subscriptionConfirmed}
              isStartFreeTrial={subscriptionManager.isStartFreeTrial}
              onGetStarted={() => {
                subscriptionManager.GetStarted();
              }}
              onStartFreeTrial={() => {
                subscriptionManager.StartFreeTrial();
              }}
              onBillingCycleChange={handleBillingCycleChange}
              addPaymentDetailsProps={{
                cardId: subscriptionManager.cardId,
                onSetAddCardFormVisible:
                  subscriptionManager.SetAddCardFormVisible,
                addCardFormVisible: subscriptionManager.addCardFormVisible,
                productType: subscriptionManager.productType,
                onCreditCardSelected: subscriptionManager.OnCreditCardSelected,
                componentMode: subscriptionManager.componentMode,
                creditCards:
                  subscriptionManager.creditCards ||
                  subscriptionManager?.paymentSpace?.creditCards,
                manager: subscriptionManager,
                billingCycle: subscriptionManager.billingCycle,
                annualPriceDetails: subscriptionManager.annualPriceDetails,
                seats: subscriptionManager.seats,
                totalPrice: subscriptionManager.totalPrice,
              }}
            />
          )}
        </div>
      </>
    );
  }),
);
